import { MatchedOnBadges } from '@/components/MatchedOnBadges/MatchedOnBadges'
import { useSearchOrganizations } from '@/generated/http-clients/v3ApiComponents'
import type {
  OrgSearchResponse,
  OrgSearchResult,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  ORG_SEARCH_MIN_LENGTH,
  deriveOrgSearchState,
  displayValue,
} from '@/helpers/orgSearch'
import { useDebounce } from '@/helpers/useDebounce'
import {
  Box,
  Button,
  Combobox,
  Field,
  HStack,
  IconButton,
  Portal,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { X } from 'lucide-react'
import {
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  ORG_PICKER_DEBOUNCE_MS,
  ORG_PICKER_LIMIT,
  type OrgPickerStatus,
  deriveOrgPickerStatus,
  findExactOrg,
  orgPickerQueryKey,
} from './orgPickerState'

export type OrgPickerProps = {
  value: string
  onChange: (orgId: string, org: OrgSearchResult | undefined) => void
  label: string
  id?: string
  error?: string
  helperText?: string
  required?: boolean
  onBlur?: () => void
  onTextSubmit?: (text: string) => void
}

const NO_RESULTS: OrgSearchResult[] = []

function okResultsOf(response: OrgSearchResponse | undefined) {
  return response?.outcome === 'ok' ? response.results : []
}

export function OrgPicker({
  value,
  onChange,
  label,
  id = 'org-picker',
  error,
  helperText,
  required = false,
  onBlur,
  onTextSubmit,
}: Readonly<OrgPickerProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()

  const cachedExact = (candidate: string) =>
    findExactOrg(
      candidate,
      okResultsOf(
        queryClient.getQueryData<OrgSearchResponse>(
          orgPickerQueryKey(candidate.trim()),
        ),
      ),
    )

  const [inputValue, setInputValue] = useState(value)
  const [previousValue, setPreviousValue] = useState(value)
  const [reportedValue, setReportedValue] = useState(value)
  const [pickedOrg, setPickedOrg] = useState(() => cachedExact(value))
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null)

  if (value !== previousValue) {
    setPreviousValue(value)
    if (value !== reportedValue) {
      setReportedValue(value)
      setInputValue(value)
      setPickedOrg(cachedExact(value))
    }
  }

  const term = useDebounce(inputValue.trim(), ORG_PICKER_DEBOUNCE_MS)
  const search = useSearchOrganizations(
    { queryParams: { q: term, limit: ORG_PICKER_LIMIT } },
    {
      enabled: term.length >= ORG_SEARCH_MIN_LENGTH && pickedOrg?.id !== term,
    },
  )
  const searchState = deriveOrgSearchState(term, search)
  const okResults =
    searchState.kind === 'results' ? searchState.results : NO_RESULTS
  const resolvedOrg =
    pickedOrg?.id === inputValue.trim()
      ? pickedOrg
      : findExactOrg(inputValue, okResults)

  const collection = useMemo(
    () =>
      createListCollection({
        items: okResults,
        itemToValue: (org) => org.id,
        itemToString: (org) => org.id,
      }),
    [okResults],
  )

  const onChangeRef = useRef(onChange)
  const resolvedRef = useRef(resolvedOrg)
  useLayoutEffect(() => {
    onChangeRef.current = onChange
    resolvedRef.current = resolvedOrg
  })

  const resolvedId = resolvedOrg?.id ?? ''

  useEffect(() => {
    if (resolvedId === value) {
      return
    }
    setReportedValue(resolvedId)
    onChangeRef.current(resolvedId, resolvedRef.current)
  }, [resolvedId, value])

  const pick = (org: OrgSearchResult | undefined) => {
    if (!org) {
      return
    }
    setPickedOrg(org)
    setInputValue(org.id)
    queryClient.setQueryData(orgPickerQueryKey(org.id), {
      outcome: 'ok',
      truncated: false,
      results: [org],
    } satisfies OrgSearchResponse)
  }

  const clear = () => {
    setInputValue('')
    setPickedOrg(undefined)
  }

  const handleEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') {
      return
    }
    if (isOpen && highlightedValue !== null) {
      event.preventDefault()
      return
    }
    const text = inputValue.trim()
    if (text !== '') {
      onTextSubmit?.(text)
    }
  }

  const hasError = Boolean(error)

  return (
    <Field.Root required={required} invalid={hasError}>
      <Combobox.Root
        collection={collection}
        inputValue={inputValue}
        onInputValueChange={(details) => {
          if (details.reason === 'input-change') {
            setInputValue(details.inputValue)
          }
        }}
        value={resolvedOrg ? [resolvedOrg.id] : []}
        onValueChange={(details) => pick(details.items[0])}
        allowCustomValue
        selectionBehavior="preserve"
        lazyMount
        unmountOnExit
        ids={{ input: id }}
        invalid={hasError}
        required={required}
        onOpenChange={(details) => setIsOpen(details.open)}
        onHighlightChange={(details) =>
          setHighlightedValue(details.highlightedValue)
        }
      >
        <Combobox.Label htmlFor={id}>
          {label}
          <Field.RequiredIndicator ml={1} />
        </Combobox.Label>
        <Combobox.Control onKeyDown={handleEnter}>
          <Combobox.Input
            placeholder={String(t('orgPicker.placeholder'))}
            fontFamily="mono"
            backgroundColor="bg"
            borderColor={hasError ? 'content.error' : 'border'}
            onBlur={onBlur}
          />
          <Combobox.IndicatorGroup>
            {inputValue === '' ? null : (
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={String(t('orgPicker.clear'))}
                onClick={clear}
              >
                <X />
              </IconButton>
            )}
          </Combobox.IndicatorGroup>
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner>
            <Combobox.Content maxHeight="320px" overflowY="auto">
              {okResults.map((org) => (
                <Combobox.Item key={org.id} item={org}>
                  <OrgOption org={org} />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
      <Box aria-live="polite" width="full">
        {resolvedOrg ? (
          <OrgConfirmation org={resolvedOrg} />
        ) : (
          <OrgPickerStatusLine
            status={deriveOrgPickerStatus(inputValue, term, searchState)}
            onRetry={() => search.refetch()}
            isRetrying={search.isFetching}
          />
        )}
      </Box>
      {hasError ? (
        <Field.ErrorText>{error}</Field.ErrorText>
      ) : (
        <Field.HelperText>
          {helperText ?? String(t('orgPicker.helper'))}
        </Field.HelperText>
      )}
    </Field.Root>
  )
}

function OrgOption({ org }: Readonly<{ org: OrgSearchResult }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  return (
    <VStack align="start" gap={1} minWidth={0} width="full">
      <Text
        fontWeight="medium"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        maxWidth="full"
      >
        {displayValue(org.name)}
      </Text>
      <HStack gap={2} maxWidth="full">
        <Text fontFamily="mono" fontSize="sm">
          {org.id}
        </Text>
        <Text
          fontSize="sm"
          color="text.text-subtle"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {t('orgPicker.ownerEmail', {
            ownerEmail: displayValue(org.ownerEmail),
          })}
        </Text>
      </HStack>
      <MatchedOnBadges
        matchedOn={org.matchedOn}
        matchedEmails={org.matchedEmails}
      />
    </VStack>
  )
}

function OrgConfirmation({ org }: Readonly<{ org: OrgSearchResult }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  return (
    <Box
      borderLeftWidth="3px"
      borderColor="border.emphasized"
      pl={3}
      py={1}
      mt={2}
    >
      <Text fontSize="xs" color="text.text-subtle">
        {t('orgPicker.selected.title')}
      </Text>
      <Text fontWeight="medium">{displayValue(org.name)}</Text>
      <Text fontFamily="mono" fontSize="sm">
        {org.id}
      </Text>
      <Text fontSize="sm">
        {t('orgPicker.ownerEmail', {
          ownerEmail: displayValue(org.ownerEmail),
        })}
      </Text>
    </Box>
  )
}

type StatusLineProps = {
  status: OrgPickerStatus
  onRetry: () => void
  isRetrying: boolean
}

function statusText(
  status: Exclude<OrgPickerStatus, { kind: 'none' }>,
  t: TFunction,
): string {
  switch (status.kind) {
    case 'tooShort':
      return String(t('orgPicker.status.tooShort'))
    case 'loading':
      return String(t('orgPicker.status.loading'))
    case 'empty':
      return String(t('orgPicker.status.empty', { query: status.query }))
    case 'unconfirmed':
      return String(t('orgPicker.status.unconfirmed'))
    case 'truncated':
      return String(t('orgPicker.status.truncated'))
    case 'invalid':
      return status.message ?? String(t('orgPicker.status.invalidFallback'))
    case 'unavailable':
      return String(t('orgPicker.status.unavailable'))
  }
}

function OrgPickerStatusLine({
  status,
  onRetry,
  isRetrying,
}: Readonly<StatusLineProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  if (status.kind === 'none') {
    return null
  }
  const isError = status.kind === 'invalid' || status.kind === 'unavailable'
  const canRetry = status.kind === 'unavailable' && status.canRetry
  return (
    <HStack gap={2} mt={2} align="center">
      <Text
        fontSize="sm"
        color={isError ? 'content.error' : 'text.text-subtle'}
      >
        {statusText(status, t)}
      </Text>
      {canRetry ? (
        <Button
          size="xs"
          variant="outline"
          onClick={onRetry}
          loading={isRetrying}
        >
          {t('orgPicker.status.retry')}
        </Button>
      ) : null}
    </HStack>
  )
}
